// RPTLAYOUT1-F1-S3 (docs/build-briefs/RPTLAYOUT1-F1-S3-guided-report-builder.md; PRD P0.13; design/mockups/reports-experience.html
// builder scenes): the guided custom report builder, one component for the location and franchisor consoles, desktop sized
// (1440x900). Start (Describe it or Build it step by step), five steps, a live preview from step 2, and a done screen.
//
// The page holds no authority. Every spec is built by reportBuilderFlowModel.ts from the older builder's own functions and the
// server validates and runs it; a figure on this screen is the server's. Text is set with textContent only. Gates read
// permissions through host.can and the server's own answers (AI status, audience choices, schedule choices), never a role name.
// Colors are --color-* tokens, so Snip & Sip Light and Ink both read.

import { compositePresentation } from "./compositeReportModel.ts";
import type { CustomReportResultDto } from "./api.ts";
import { CR_CUSTOM_DATES, valueLabelsOf, type CatalogDataset } from "./customReportModel.ts";
import type { NetworkCatalogDto } from "./franchisorCustomReportsModel.ts";
import type { ReportResultsHost } from "./reportBuilderResults.ts";
import { mountLocationPicker } from "./locationPicker.ts";
import type { PickerMode } from "./locationPickerModel.ts";
import { CHART_WIDTH_BUILDER, renderChartCard } from "./reportChart.ts";
import { buildChartModel, chartOf } from "./reportChartModel.ts";
import {
  AI_IDEAS,
  BUILD_PERMISSION,
  CHART_CHOICES,
  FLOW_STEPS,
  LAST_DAY,
  MONTH_DAYS,
  SCHEDULE_TIMES,
  WEEKDAY_NAMES,
  addFlowFilter,
  aiDescription,
  aiNeedsAcknowledgement,
  aiOffered,
  chartAllowed,
  COMPARE_OPTIONS,
  compareAllowed,
  setCompare,
  datasetOf,
  datesApply,
  filterFieldChoices,
  flowCatalog,
  flowFromSpec,
  flowSpec,
  freshFlow,
  furthestStep,
  groupChoices,
  isAiStatus,
  joinSentence,
  monthDayLabel,
  nextStep,
  numberChoices,
  presetChips,
  previewShows,
  previewSpec,
  previewStatusOf,
  previewTable,
  previewSummary,
  previousStep,
  removeFlowFilter,
  sameSpec,
  savePlan,
  saveProblem,
  scheduleBody,
  searchSources,
  setChartType,
  setCustomDates,
  setDimension,
  setPreset,
  setScope,
  setShow,
  sourceBlocked,
  stepReady,
  toggleNumber,
  toggleSource,
  updateFlowFilter,
  type AiStatus,
  type FlowCatalog,
  type FlowState,
  type PreviewStatus,
  type SaveStepId,
  type StepNumber,
} from "./reportBuilderFlowModel.ts";
import { hideHoverHelp, hoverHelp } from "./hoverHelp.ts";

export interface ReportBuilderHost extends ReportResultsHost {
  /** Open a saved report in the results screen. */
  openSaved(id: string, version: number): void;
  /** Edit a saved report as a new version: its spec, id and the version it was opened at. */
  editing?: { spec: unknown; id: string; baseVersion: number };
}

export interface ReportBuilderFrame {
  destroy(): void;
}

type View = "loading" | "failed" | "start" | "ai" | "steps" | "done";
interface Choice {
  id: string;
  name: string;
}
interface ScheduleChoices {
  recipients: Choice[];
  available: boolean;
  reason: string;
}
interface SavedReport {
  id: string;
  version: number;
  audienceRevision: number;
}
interface SaveOutcome {
  step: SaveStepId;
  ok: boolean;
  message: string;
}

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== undefined) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}
function button(text: string, className: string, action: () => void, testid?: string): HTMLButtonElement {
  const element = node("button", className, text);
  element.type = "button";
  element.addEventListener("click", action);
  if (testid !== undefined) element.dataset["testid"] = testid;
  return element;
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function message(reply: { status: number; body: unknown }, fallback: string): string {
  return isRecord(reply.body) && typeof reply.body["message"] === "string" ? reply.body["message"] : fallback;
}

const HELP = {
  builder: ["Custom report", "Build a report from the numbers we keep, then save it, share it or email it on a schedule.", "Under Reports, New custom report.", "Net sales by employee last month."],
  sources: ["Sources", "Tick every source the report needs. Several sources are joined on what they share.", "Step 1 of the builder.", "Sales by day with Tips by day."],
  preview: ["Live preview", "A small run of the report as you build it. The saved report shows every row.", "Beside steps 2 to 5.", "Change a number and the preview follows."],
  oneAtATime: ["One report at a time", "Only one report runs at a time at each location. The preview resumes when the current report finishes.", "Wherever a report runs.", "A scheduled report running at the same location."],
  billed: ["Billed separately", "AI help is charged on top of your plan and listed on the monthly statement with the name of the person who used it.", "Describe it on the first screen.", "Each description you send is one use."],
  compare: ["Compare", "Show each number beside the same number from an earlier period, with the change. Leave it on None to see the report on its own.", "Step 4 of the builder.", "Last 30 days against the 30 days before."],
  share: ["Share", "Only me keeps the report private. Named people lets the people you pick open it.", "Step 5 of the builder.", "Share a weekly report with your lounge managers."],
  schedule: ["Email it on a schedule", "The report is emailed at the time you choose, in the location's time.", "Step 5 of the builder.", "Every Monday at 8:00 AM."],
} as const;
function help<T extends HTMLElement>(target: T, key: keyof typeof HELP): T {
  const [, what, where, example] = HELP[key];
  hoverHelp(target, what, where, example);
  return target;
}

export function mountReportBuilderFlow(target: HTMLElement, host: ReportBuilderHost): ReportBuilderFrame {
  let alive = true;
  let generation = 0;
  let view: View = "loading";
  let step: StepNumber = 1;
  let catalog: FlowCatalog | undefined;
  let tree: NetworkCatalogDto["franchisees"] | undefined;
  let loadError = "";
  let flow: FlowState = freshFlow();
  let search = "";
  let aiStatus: AiStatus | undefined;
  let aiText = "";
  let aiAsk = false;
  let aiBusy = false;
  let aiMessage = "";
  let aiSuggestions: string[] = [];
  let aiNotice = "";
  let aiRequestId: string | undefined;
  let datesCustom = false;
  let popover: "locations" | null = null;
  let adding = false;
  let people: Choice[] | undefined;
  let peopleNote = "";
  let schedule: ScheduleChoices | undefined;
  let choicesRequested = false;
  let saving = false;
  let saveError = "";
  let saved: SavedReport | undefined;
  /** REPORTUX1: the spec that was saved, so Edit report on the done screen reopens exactly it. */
  let savedSpec: unknown;
  let outcomes: SaveOutcome[] = [];
  const requestId = crypto.randomUUID();
  // The preview.
  let previewStatus: PreviewStatus = "idle";
  let previewResult: CustomReportResultDto | undefined;
  let previewNote = "";
  let previewRunning = false;
  let previewQueued = false;
  let previewSpecRan: unknown;
  let previewTimer: ReturnType<typeof setTimeout> | undefined;

  const root = node("section", "rx rb");
  root.dataset["testid"] = "rb";
  const screen = node("div", "rb-screen");
  root.append(screen);
  target.append(root);
  const active = () => alive && host.active() && root.isConnected;
  const canBuild = () => host.can(BUILD_PERMISSION[host.kind]);

  let nextButton: HTMLButtonElement | undefined;
  let saveButton: HTMLButtonElement | undefined;
  let saveReason: HTMLElement | undefined;
  const saveReasonId = `report-save-reason-${crypto.randomUUID()}`;
  const scheduleReasonId = `report-schedule-reason-${crypto.randomUUID()}`;
  let stepper: HTMLElement | undefined;
  let left: HTMLElement | undefined;
  let previewBox: HTMLElement | undefined;

  // ---------------------------------------------------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------------------------------------------------
  async function load(): Promise<void> {
    const captured = ++generation;
    view = "loading";
    draw();
    try {
      const reply = await host.request<unknown>("GET", `${host.customPrefix}/catalog`);
      if (!active() || captured !== generation) return;
      const body = reply.body;
      const shaped = reply.status === 200 && isRecord(body) && isRecord(body["catalog"]) && Array.isArray((body["catalog"] as Record<string, unknown>)["datasets"]) && Array.isArray(body["locations"]);
      if (!shaped) {
        loadError = message(reply, "The report builder did not load. Try again.");
        view = "failed";
        draw();
        return;
      }
      const dto = body as unknown as Parameters<typeof flowCatalog>[1];
      catalog = flowCatalog(host.kind, dto);
      tree = host.kind === "network" && "franchisees" in dto ? (dto as NetworkCatalogDto).franchisees : undefined;
    } catch {
      if (!active() || captured !== generation) return;
      loadError = "The report builder did not load. Try again.";
      view = "failed";
      draw();
      return;
    }
    if (host.editing !== undefined && catalog !== undefined) {
      const held = flowFromSpec(host.editing.spec, catalog, tree);
      if (held === undefined) {
        loadError = "This report cannot be edited here.";
        view = "failed";
        draw();
        return;
      }
      flow = held;
      datesCustom = held.dates === CR_CUSTOM_DATES;
      step = 1;
      view = "steps";
      draw();
      return;
    }
    view = "start";
    draw();
    void loadAiStatus();
  }

  async function loadAiStatus(): Promise<void> {
    try {
      const where = host.locationId === undefined ? "" : `?locationId=${encodeURIComponent(host.locationId)}`;
      const reply = await host.request<unknown>("GET", `${host.customPrefix}/ai/status${where}`);
      if (!active()) return;
      aiStatus = reply.status === 200 && isAiStatus(reply.body) ? reply.body : undefined;
    } catch {
      aiStatus = undefined;
    }
    if (active() && view === "start") draw();
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // The live preview (DEC-435: one run at a time; a change during a run queues exactly one rerun)
  // ---------------------------------------------------------------------------------------------------------------------
  function changed(): void {
    updateNav();
    if (view === "steps" && previewShows(step)) {
      if (previewTimer !== undefined) clearTimeout(previewTimer);
      previewTimer = setTimeout(() => void runPreview(), 350);
    }
  }
  async function runPreview(): Promise<void> {
    if (!active() || catalog === undefined || view !== "steps" || !previewShows(step) || !canBuild()) return;
    // The name is not part of what a run reads, so typing it never reruns the preview.
    const built = previewSpec({ ...flow, name: "" }, catalog);
    if (!built.ok) {
      previewStatus = "idle";
      previewResult = undefined;
      previewSpecRan = undefined;
      drawPreview();
      return;
    }
    if (previewRunning) {
      previewQueued = true;
      return;
    }
    if (sameSpec(built.spec, previewSpecRan) && (previewStatus === "ok" || previewStatus === "empty")) return;
    previewRunning = true;
    previewStatus = "loading";
    previewNote = "";
    drawPreview();
    const captured = generation;
    try {
      const reply = await host.request<unknown>("POST", `${host.customPrefix}/run`, { spec: built.spec });
      if (!active() || captured !== generation) return;
      previewStatus = previewStatusOf(reply);
      previewResult = previewStatus === "ok" ? (reply.body as CustomReportResultDto) : undefined;
      previewSpecRan = previewStatus === "ok" || previewStatus === "empty" ? built.spec : undefined;
      previewNote = previewStatus === "error" || previewStatus === "denied" ? message(reply, "The preview did not load. Try again.") : "";
    } catch {
      if (!active() || captured !== generation) return;
      previewStatus = "error";
      previewNote = "The preview did not load. Try again.";
    } finally {
      previewRunning = false;
    }
    if (!active()) return;
    drawPreview();
    if (previewQueued) {
      previewQueued = false;
      void runPreview();
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Drawing
  // ---------------------------------------------------------------------------------------------------------------------
  function draw(): void {
    if (!active()) return;
    hideHoverHelp();
    nextButton = undefined;
    saveButton = undefined;
    saveReason = undefined;
    stepper = undefined;
    left = undefined;
    previewBox = undefined;
    screen.replaceChildren(header());
    if (view === "loading") screen.append(loadingCard());
    else if (view === "failed") screen.append(failedCard());
    else if (view === "start") screen.append(startScreen());
    else if (view === "ai") screen.append(aiScreen());
    else if (view === "done") screen.append(doneScreen());
    else screen.append(stepsScreen());
    if (view === "steps" && previewShows(step)) drawPreview();
  }
  function header(): HTMLElement {
    const row = node("div", "rb-head");
    row.append(button("‹ Reports", "rx-back", () => host.back(), "rb-back-reports"));
    row.append(help(node("h1", undefined, host.editing === undefined ? "New custom report" : "Edit report"), "builder"));
    return row;
  }
  function loadingCard(): HTMLElement {
    const card = node("div", "rx-card rx-state");
    card.dataset["testid"] = "rb-loading";
    card.setAttribute("role", "status");
    card.setAttribute("aria-label", "Loading the report builder");
    for (const width of ["40%", "100%", "70%"]) {
      const line = node("div", "rx-skel");
      line.style.width = width;
      card.append(line);
    }
    return card;
  }
  function failedCard(): HTMLElement {
    const card = node("div", "rx-card rx-state");
    card.dataset["testid"] = "rb-failed";
    card.setAttribute("role", "alert");
    card.append(node("h2", undefined, "The builder did not load"), node("p", undefined, loadError));
    const actions = node("div", "rx-actions");
    actions.append(button("Try again", "rx-btn rx-btn-primary", () => void load(), "rb-retry"), button("Back to Reports", "rx-btn", () => host.back()));
    card.append(actions);
    return card;
  }

  // ----- start
  function startScreen(): HTMLElement {
    const wrap = node("div", "rb-start");
    if (!canBuild()) {
      const state = node("div", "rx-card rx-state");
      state.dataset["testid"] = "rb-denied";
      state.setAttribute("role", "alert");
      state.append(node("h2", undefined, "No access to build reports"), node("p", undefined, "Ask an admin."));
      return state;
    }
    const ai = node("div", "rb-choice-wrap");
    const aiChoice = node("button", "rb-choice");
    aiChoice.type = "button";
    aiChoice.dataset["testid"] = "rb-choice-ai";
    aiChoice.append(node("span", "rb-big", "AI"), node("h2", undefined, "Describe it"), node("span", "rb-muted", "Say what you want to see"));
    aiChoice.disabled = !aiOffered(aiStatus);
    // UIFIX5: a card that cannot be used says why in words, not only by looking dim.
    if (aiChoice.disabled && aiStatus !== undefined) aiChoice.append(node("span", "rb-muted", "Not available yet"));
    aiChoice.addEventListener("click", () => {
      if (aiStatus === undefined) return;
      view = "ai";
      aiAsk = aiNeedsAcknowledgement(aiStatus);
      aiMessage = "";
      aiSuggestions = [];
      draw();
    });
    ai.append(aiChoice);
    if (aiOffered(aiStatus)) {
      const bill = node("span", "rb-bill");
      bill.append(help(node("span", undefined, "Billed separately"), "billed"));
      ai.append(bill);
    }
    const manual = node("div", "rb-choice-wrap");
    const manualChoice = node("button", "rb-choice");
    manualChoice.type = "button";
    manualChoice.dataset["testid"] = "rb-choice-step";
    manualChoice.append(node("span", "rb-big", "1 2 3"), node("h2", undefined, "Build it step by step"), node("span", "rb-muted", "Pick sources, numbers and filters"));
    manualChoice.addEventListener("click", () => {
      view = "steps";
      step = 1;
      draw();
    });
    manual.append(manualChoice);
    wrap.append(ai, manual);
    return wrap;
  }

  // ----- Describe it
  function aiScreen(): HTMLElement {
    const wrap = node("div");
    const card = node("div", "rx-card rb-pane rb-ai");
    card.dataset["testid"] = "rb-ai";
    const top = node("div", "rb-row");
    top.append(node("h2", undefined, "Describe your report"), node("span", "rx-grow"));
    const bill = node("span", "rb-bill");
    bill.append(help(node("span", undefined, "Billed separately"), "billed"));
    top.append(bill);
    card.append(top);
    const text = node("textarea", "rx-field rb-ai-text");
    text.maxLength = 2000;
    text.rows = 4;
    text.value = aiText;
    text.placeholder = "Example: net sales and tips by employee last month";
    text.setAttribute("aria-label", "Describe your report");
    text.dataset["testid"] = "rb-ai-text";
    text.addEventListener("input", () => {
      aiText = text.value;
      aiRequestId = undefined;
      build.disabled = aiBusy || aiDescription(aiText) === "";
    });
    card.append(text);
    card.append(node("div", "rx-label rb-gap", "Or tap an idea"));
    const ideas = node("div", "rb-chips");
    for (const idea of AI_IDEAS) {
      ideas.append(
        button(idea, "rx-chip-btn", () => {
          aiText = idea;
          aiRequestId = undefined;
          draw();
        }),
      );
    }
    card.append(ideas);
    if (aiMessage !== "") {
      const note = node("p", "rx-notice", aiMessage);
      note.setAttribute("role", "status");
      note.dataset["testid"] = "rb-ai-message";
      card.append(note);
      if (aiSuggestions.length > 0) {
        const suggestions = node("div", "rb-chips");
        for (const suggestion of aiSuggestions) {
          suggestions.append(
            button(suggestion.slice(0, 140), "rx-chip-btn", () => {
              aiText = suggestion;
              aiRequestId = undefined;
              void draft();
            }),
          );
        }
        card.append(suggestions);
      }
    }
    const foot = node("div", "rb-foot");
    foot.append(
      button("Build it step by step", "rx-btn", () => {
        view = "steps";
        step = 1;
        draw();
      }, "rb-ai-manual"),
      node("span", "rx-grow"),
    );
    const build = button(aiBusy ? "Building" : "Build it", "rx-btn rx-btn-primary", () => void draft(), "rb-ai-build");
    build.disabled = aiBusy || aiDescription(aiText) === "" || aiAsk;
    foot.append(build);
    card.append(foot);
    wrap.append(card);
    if (aiAsk && aiStatus !== undefined) wrap.append(acknowledgement(aiStatus));
    return wrap;
  }
  function acknowledgement(status: AiStatus): HTMLElement {
    const layer = node("div", "rb-dialog-layer");
    const scrim = node("div", "rb-scrim");
    const dialog = node("div", "rb-dialog");
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-label", "AI help is billed separately");
    dialog.dataset["testid"] = "rb-ack";
    dialog.append(node("h2", undefined, "AI help is billed separately"));
    dialog.append(node("p", "rb-ack-text", status.rateLine ?? "Each use is charged and listed on your monthly statement with your name."));
    const failure = node("p", "rx-notice");
    failure.setAttribute("role", "alert");
    failure.hidden = true;
    dialog.append(failure);
    const actions = node("div", "rb-foot");
    const yes = button("I understand", "rx-btn rx-btn-primary", () => void acknowledge(yes, failure), "rb-ack-yes");
    yes.disabled = status.termsVersion === null;
    actions.append(
      button("Cancel", "rx-btn", () => {
        aiAsk = false;
        view = "start";
        draw();
      }, "rb-ack-cancel"),
      node("span", "rx-grow"),
      yes,
    );
    dialog.append(actions);
    layer.append(scrim, dialog);
    // Escape cancels, as the Cancel button does; the confirming button takes focus so the keyboard and a screen reader land on it.
    layer.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        aiAsk = false;
        view = "start";
        draw();
      }
    });
    queueMicrotask(() => yes.focus());
    return layer;
  }
  async function acknowledge(yes: HTMLButtonElement, failure: HTMLElement): Promise<void> {
    if (aiStatus === undefined || aiStatus.termsVersion === null) return;
    yes.disabled = true;
    const where = host.locationId === undefined ? "" : `?locationId=${encodeURIComponent(host.locationId)}`;
    try {
      const reply = await host.request<unknown>("POST", `${host.customPrefix}/ai/acknowledge${where}`, { termsVersion: aiStatus.termsVersion });
      if (!active()) return;
      if (reply.status === 200 && isAiStatus(reply.body)) {
        aiStatus = reply.body;
        aiAsk = false;
        draw();
        return;
      }
      failure.textContent = message(reply, "AI help is unavailable right now.");
    } catch {
      if (!active()) return;
      failure.textContent = "AI help is unavailable right now.";
    }
    failure.hidden = false;
    yes.disabled = false;
  }
  async function draft(): Promise<void> {
    if (aiBusy || aiAsk || catalog === undefined) return;
    const description = aiDescription(aiText);
    if (description === "") return;
    aiBusy = true;
    aiMessage = "";
    aiSuggestions = [];
    draw();
    aiRequestId ??= crypto.randomUUID();
    const where = host.locationId === undefined ? "" : `?locationId=${encodeURIComponent(host.locationId)}`;
    try {
      const reply = await host.request<unknown>("POST", `${host.customPrefix}/ai/draft${where}`, { requestId: aiRequestId, reportAiDescription: description });
      if (!active()) return;
      aiBusy = false;
      const body = isRecord(reply.body) ? reply.body : {};
      const held = reply.status === 200 && body["spec"] !== undefined ? flowFromSpec(body["spec"], catalog, tree) : undefined;
      if (held !== undefined) {
        flow = held;
        datesCustom = held.dates === CR_CUSTOM_DATES;
        aiNotice = [body["explanation"], body["disclosure"]].filter((part): part is string => typeof part === "string" && part !== "").join(" ");
        view = "steps";
        step = 4;
        draw();
        changed();
        return;
      }
      aiMessage = reply.status === 200 ? "I couldn't build that. Try describing it another way." : message(reply, "I couldn't build that.");
      aiSuggestions = Array.isArray(body["suggestions"]) ? body["suggestions"].filter((entry): entry is string => typeof entry === "string").slice(0, 3) : [];
      if (reply.status === 409) void loadAiStatus();
    } catch {
      if (!active()) return;
      aiBusy = false;
      aiMessage = "AI help is unavailable right now. You can still build the report yourself.";
    }
    draw();
  }

  // ----- steps
  function stepsScreen(): HTMLElement {
    const wrap = node("div");
    wrap.append(stepsHeader());
    const work = node("div", `rb-work${previewShows(step) ? "" : " full"}`);
    const pane = node("div", "rx-card rb-pane");
    pane.dataset["testid"] = `rb-step-${step}`;
    left = node("div", "rb-body");
    pane.append(left, navigation());
    work.append(pane);
    if (previewShows(step)) {
      const box = node("div", "rx-card rb-preview");
      box.dataset["testid"] = "rb-preview";
      previewBox = box;
      work.append(box);
    }
    wrap.append(work);
    drawLeft();
    updateNav();
    return wrap;
  }
  function stepsHeader(): HTMLElement {
    const strip = node("div", "rb-steps");
    strip.setAttribute("role", "list");
    stepper = strip;
    const reach = catalog === undefined ? 1 : furthestStep(flow, catalog);
    FLOW_STEPS.forEach((name, at) => {
      const number = (at + 1) as StepNumber;
      const item = node("button", `rb-stp${step === number ? " on" : step > number ? " done" : ""}`);
      item.type = "button";
      item.dataset["testid"] = `rb-stepper-${number}`;
      item.disabled = number > reach && number > step;
      item.setAttribute("aria-current", step === number ? "step" : "false");
      item.append(node("i", undefined, step > number ? "✓" : String(number)), document.createTextNode(name));
      item.addEventListener("click", () => goTo(number));
      strip.append(item);
      if (number < 5) strip.append(node("span", `rb-line${step > number ? " done" : ""}`));
    });
    return strip;
  }
  function navigation(): HTMLElement {
    const foot = node("div", "rb-foot");
    const back = button("Back", "rx-btn", () => {
      const before = previousStep(step);
      if (before === 0) {
        view = "start";
        draw();
        void loadAiStatus();
      } else goTo(before);
    }, "rb-back");
    foot.append(back, node("span", "rx-grow"));
    if (step === 5) {
      const confirm = button(saving ? "Saving" : host.editing === undefined ? "Save report" : "Save new version", "rx-btn rx-btn-primary rb-big-btn", () => void save(), "rb-save");
      saveButton = confirm;
      confirm.setAttribute("aria-describedby", saveReasonId);
      foot.append(confirm);
    } else {
      const forward = button("Next", "rx-btn rx-btn-primary rb-big-btn", () => goTo(nextStep(step)), "rb-next");
      nextButton = forward;
      foot.append(forward);
    }
    return foot;
  }
  function updateNav(): void {
    if (catalog === undefined) return;
    if (nextButton !== undefined) nextButton.disabled = !stepReady(flow, step, catalog);
    const problem = saveProblem(flow, catalog);
    if (saveButton !== undefined) saveButton.disabled = saving || problem !== "";
    if (saveReason !== undefined) {
      saveReason.textContent = saving ? "" : problem;
      saveReason.hidden = saving || problem === "";
    }
  }
  function goTo(next: StepNumber): void {
    if (catalog === undefined) return;
    if (next > step && !stepReady(flow, step, catalog)) return;
    step = next;
    popover = null;
    adding = false;
    if (step === 5) void loadChoices();
    draw();
    changed();
  }
  function drawLeft(): void {
    if (left === undefined || catalog === undefined) return;
    const focused = document.activeElement instanceof HTMLElement ? document.activeElement.dataset["testid"] : undefined;
    left.replaceChildren(...(step === 1 ? sourcesPane(catalog) : step === 2 ? numbersPane(catalog) : step === 3 ? filtersPane(catalog) : step === 4 ? groupPane(catalog) : savePane(catalog)));
    if (focused !== undefined) left.querySelector<HTMLElement>(`[data-testid="${focused}"]`)?.focus();
    if (step === 1 || step >= 2) refreshStepper();
  }
  function refreshStepper(): void {
    // The stepper's reach follows the choices (a later step opens when the earlier ones are ready).
    if (stepper === undefined || catalog === undefined) return;
    const reach = furthestStep(flow, catalog);
    stepper.querySelectorAll<HTMLButtonElement>("button.rb-stp").forEach((item, at) => {
      const number = at + 1;
      item.disabled = number > reach && number > step;
    });
  }
  function change(next: FlowState, redraw = true): void {
    flow = next;
    if (redraw) drawLeft();
    changed();
  }
  function heading(text: string, key?: keyof typeof HELP): HTMLElement {
    const row = node("div", "rb-row");
    const title = node("h2", undefined, text);
    row.append(key === undefined ? title : help(title, key));
    return row;
  }
  /** REPORTUX1 (baseline 11): a chip is a radio when the choices in its group exclude each other, a checkbox when several may be on. */
  function chip(label: string, on: boolean, action: () => void, testid: string, disabled = false, kind: "radio" | "checkbox" = "checkbox"): HTMLButtonElement {
    const element = button(label, `rx-chip-btn${on ? " on" : ""}`, action, testid);
    element.setAttribute("role", kind);
    element.setAttribute("aria-checked", String(on));
    element.disabled = disabled;
    return element;
  }
  /** One radio of a segmented control: the same button, with radio semantics instead of a pressed toggle. */
  function segRadio(toggle: HTMLButtonElement, on: boolean): HTMLButtonElement {
    toggle.setAttribute("role", "radio");
    toggle.setAttribute("aria-checked", String(on));
    return toggle;
  }
  /**
   * REPORTUX1 (baseline 11): make a row of radios one radio group. It is labelled, has one tab stop (the checked radio, or the
   * first enabled one when none is checked), and the arrow keys move the choice to the next enabled radio the way a native
   * radio group does. Call it after the radios are appended.
   */
  function radioGroup(group: HTMLElement, label: string): HTMLElement {
    group.setAttribute("role", "radiogroup");
    group.setAttribute("aria-label", label);
    const radios = (): HTMLButtonElement[] => Array.from(group.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
    const enabled = radios().filter((radio) => !radio.disabled);
    const stop = enabled.find((radio) => radio.getAttribute("aria-checked") === "true") ?? enabled[0];
    for (const radio of radios()) radio.tabIndex = radio === stop ? 0 : -1;
    group.addEventListener("keydown", (event) => {
      const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
      const backward = event.key === "ArrowLeft" || event.key === "ArrowUp";
      if (!forward && !backward) return;
      const live = radios().filter((radio) => !radio.disabled);
      const at = live.findIndex((radio) => radio === document.activeElement);
      if (at < 0 || live.length < 2) return;
      event.preventDefault();
      const next = live[(at + (forward ? 1 : live.length - 1)) % live.length];
      // Focus first: the redraw that follows a choice puts focus back on the control that had it.
      next?.focus();
      next?.click();
    });
    return group;
  }

  // ----- step 1
  function sourcesPane(current: FlowCatalog): HTMLElement[] {
    const find = node("input", "rx-field rb-find");
    find.type = "search";
    find.placeholder = "Find sources";
    find.value = search;
    find.setAttribute("aria-label", "Find sources");
    find.dataset["testid"] = "rb-find";
    const list = node("div", "rb-sources");
    list.setAttribute("role", "group");
    list.setAttribute("aria-label", "Sources");
    const paint = (): void => {
      list.replaceChildren();
      for (const dataset of searchSources(current.datasets, search)) {
        const on = flow.sources.some((source) => source.dataset === dataset.id);
        const blocked = sourceBlocked(flow, dataset, current);
        const row = node("button", `rb-src${on ? " on" : ""}`);
        row.type = "button";
        row.dataset["testid"] = `rb-source-${dataset.id}`;
        row.setAttribute("role", "checkbox");
        row.setAttribute("aria-checked", String(on));
        row.disabled = blocked;
        row.append(node("span", `rb-ck${on ? " on" : ""}`), node("span", undefined, dataset.label));
        row.addEventListener("click", () => {
          change(toggleSource(flow, dataset.id, current), false);
          paint();
          sentence();
        });
        list.append(row);
      }
      if (list.childElementCount === 0) list.append(node("p", "rx-empty-line", "No matching sources"));
    };
    find.addEventListener("input", () => {
      search = find.value;
      paint();
    });
    const join = node("div", "rb-join");
    join.dataset["testid"] = "rb-join";
    const sentence = (): void => {
      const text = joinSentence(flow, current);
      join.textContent = text;
      join.hidden = text === "";
      refreshStepper();
    };
    paint();
    sentence();
    return [heading("Pick your sources", "sources"), find, list, join];
  }

  // ----- step 2
  function numbersPane(current: FlowCatalog): HTMLElement[] {
    const out: HTMLElement[] = [heading("Pick the numbers")];
    for (const source of flow.sources) {
      const dataset = datasetOf(current, source.dataset);
      if (dataset === undefined) continue;
      if (flow.sources.length > 1) out.push(node("div", "rx-label rb-gap", dataset.label));
      const chips = node("div", "rb-chips");
      for (const choice of numberChoices(source, current)) {
        chips.append(chip(choice.label, choice.on, () => change(toggleNumber(flow, source.dataset, choice.field, current)), `rb-number-${source.dataset}-${choice.field}`));
      }
      out.push(chips);
    }
    return out;
  }

  // ----- step 3
  function filtersPane(current: FlowCatalog): HTMLElement[] {
    const out: HTMLElement[] = [heading("Narrow it down")];
    if (datesApply(flow, current)) {
      const dates = node("div", "rb-frow");
      dates.append(node("b", "rb-flabel", "Dates"));
      const chips = node("div", "rb-chips");
      for (const preset of presetChips(current, flow.dates)) {
        chips.append(chip(preset.label, flow.dates === preset.id && !datesCustom, () => {
          datesCustom = false;
          change(setPreset(flow, preset.id));
        }, `rb-dates-${preset.id}`, false, "radio"));
      }
      chips.append(chip("Custom range", datesCustom, () => {
        datesCustom = true;
        change(setCustomDates(flow, flow.from, flow.to));
      }, "rb-dates-custom", false, "radio"));
      dates.append(radioGroup(chips, "Dates"));
      out.push(dates);
      if (datesCustom) {
        const inputs = node("div", "rx-custom-inputs rb-frow");
        for (const key of ["from", "to"] as const) {
          const field = node("label", "rx-date");
          field.append(node("span", undefined, key === "from" ? "From" : "To"));
          const input = node("input", "rx-field");
          input.type = "date";
          input.value = flow[key];
          input.max = current.businessDate;
          input.dataset["testid"] = `rb-${key}`;
          input.addEventListener("change", () => {
            change(setCustomDates(flow, key === "from" ? input.value : flow.from, key === "to" ? input.value : flow.to), false);
          });
          field.append(input);
          inputs.append(field);
        }
        out.push(inputs);
      }
    }
    if (current.locations.length > 1) out.push(locationsRow(current));
    for (const source of flow.sources) {
      const dataset = datasetOf(current, source.dataset);
      if (dataset === undefined) continue;
      for (const filter of source.filters) {
        const field = dataset.fields.find((candidate) => candidate.name === filter.field);
        if (field === undefined) continue;
        const row = node("div", "rb-frow rb-filter");
        row.dataset["testid"] = `rb-filter-${filter.id}`;
        row.append(node("b", "rb-flabel", flow.sources.length > 1 ? `${dataset.label}, ${field.label}` : field.label));
        const edit = (patch: Partial<{ value: string; value2: string }>, redraw: boolean): void => change(updateFlowFilter(flow, source.dataset, filter.id, patch), redraw);
        if (field.type === "enum" || field.type === "boolean") {
          const chips = node("div", "rb-chips");
          const values = field.type === "boolean" ? ["true", "false"] : (field.values ?? []);
          for (const value of values) {
            const label = field.type === "boolean" ? (value === "true" ? "Yes" : "No") : (field.valueLabels?.[value] ?? value);
            chips.append(chip(label, filter.value === value, () => edit({ value }, true), `rb-filter-${filter.id}-${value}`, false, "radio"));
          }
          row.append(radioGroup(chips, field.label));
        } else if (field.type === "date") {
          for (const key of ["value", "value2"] as const) {
            const input = node("input", "rx-field rb-date");
            input.type = "date";
            input.value = filter[key];
            input.max = current.businessDate;
            input.setAttribute("aria-label", key === "value" ? `${field.label} from` : `${field.label} to`);
            input.dataset["testid"] = `rb-filter-${filter.id}-${key}`;
            input.addEventListener("change", () => edit({ [key]: input.value }, false));
            row.append(input);
          }
        } else {
          const input = node("input", "rx-field");
          input.type = "text";
          input.value = filter.value;
          input.setAttribute("aria-label", `${field.label} contains`);
          input.dataset["testid"] = `rb-filter-${filter.id}-value`;
          input.addEventListener("input", () => edit({ value: input.value }, false));
          row.append(input);
        }
        row.append(button("Remove", "rx-btn rb-remove", () => change(removeFlowFilter(flow, source.dataset, filter.id)), `rb-filter-${filter.id}-remove`));
        out.push(row);
      }
    }
    const choices = filterFieldChoices(flow, current);
    if (adding) {
      const select = node("select", "rx-field rb-add-select");
      select.setAttribute("aria-label", "Filter field");
      select.dataset["testid"] = "rb-add-filter-field";
      select.append(new Option("Choose a field", ""));
      for (const choice of choices) select.append(new Option(choice.label, `${choice.source}|${choice.field}`));
      select.addEventListener("change", () => {
        const [sourceId, field] = select.value.split("|");
        adding = false;
        if (sourceId !== undefined && field !== undefined && select.value !== "") change(addFlowFilter(flow, sourceId, field, current));
        else drawLeft();
      });
      out.push(select);
    } else {
      const add = button("+ Add filter", "rx-btn", () => {
        adding = true;
        drawLeft();
        left?.querySelector<HTMLElement>('[data-testid="rb-add-filter-field"]')?.focus();
      }, "rb-add-filter");
      add.disabled = choices.length === 0;
      out.push(add);
    }
    return out;
  }
  function locationsRow(current: FlowCatalog): HTMLElement {
    const row = node("div", "rb-frow rb-relative");
    row.append(node("b", "rb-flabel", "Locations"));
    const total = current.locations.length;
    const summary =
      flow.scope.mode === "all"
        ? `All ${total} locations`
        : flow.scope.ids.length === 1
          ? (current.locations.find((site) => site.id === flow.scope.ids[0])?.name ?? "1 location")
          : `${flow.scope.ids.length} locations`;
    const open = node("button", `rx-sel${popover === "locations" ? " open" : ""}`);
    open.type = "button";
    open.dataset["testid"] = "rb-locations";
    open.setAttribute("aria-haspopup", "dialog");
    open.setAttribute("aria-expanded", String(popover === "locations"));
    open.append(node("span", "rx-sel-text", summary));
    open.addEventListener("click", () => {
      popover = popover === "locations" ? null : "locations";
      drawLeft();
    });
    row.append(open);
    if (popover === "locations") {
      const scrim = node("div", "rx-scrim");
      scrim.addEventListener("click", () => {
        popover = null;
        drawLeft();
      });
      const panel = node("div", "rx-pop locations rb-pop");
      panel.setAttribute("role", "dialog");
      panel.setAttribute("aria-label", "Locations");
      panel.dataset["testid"] = "rb-pop-locations";
      const modes: PickerMode[] = host.kind === "network" ? ["one", "selected", "all"] : ["one", "all"];
      panel.append(
        mountLocationPicker({
          locations: current.locations,
          value: flow.scope,
          modes,
          cancel: () => {
            popover = null;
            drawLeft();
          },
          apply: (picked) => {
            popover = null;
            change(setScope(flow, picked));
          },
        }),
      );
      row.append(scrim, panel);
    }
    return row;
  }

  // ----- step 4
  function groupPane(current: FlowCatalog): HTMLElement[] {
    const out: HTMLElement[] = [heading("Group and show")];
    if (aiNotice !== "") {
      const note = node("p", "rb-ai-note", aiNotice);
      note.dataset["testid"] = "rb-ai-note";
      out.push(note);
    }
    out.push(node("div", "rx-label", "One row for each"));
    const groups = node("div", "rb-chips rb-groups");
    for (const group of groupChoices(flow, current)) {
      groups.append(chip(group.label, flow.dimension === group.key, () => change(setDimension(flow, group.key)), `rb-group-${group.key}`, !group.enabled, "radio"));
    }
    out.push(radioGroup(groups, "One row for each"));
    out.push(node("div", "rx-label rb-gap", "Show as"));
    const show = node("div", "rx-seg");
    for (const choice of [
      { id: "table" as const, label: "Table" },
      { id: "chart" as const, label: "Chart" },
    ]) {
      const toggle = button(choice.label, flow.show === choice.id ? "on" : "", () => change(setShow(flow, choice.id, current)), `rb-show-${choice.id}`);
      segRadio(toggle, flow.show === choice.id);
      toggle.disabled = choice.id === "chart" && !chartAllowed(flow, current);
      show.append(toggle);
    }
    out.push(radioGroup(show, "Show as"));
    if (flow.show === "chart") {
      out.push(node("div", "rx-label rb-gap", "Chart type"));
      const types = node("div", "rx-seg");
      for (const choice of CHART_CHOICES) {
        const toggle = button(choice.label, flow.chart === choice.id ? "on" : "", () => change(setChartType(flow, choice.id, current)), `rb-chart-${choice.id}`);
        segRadio(toggle, flow.chart === choice.id);
        toggle.disabled = !chartAllowed(flow, current, choice.id);
        types.append(toggle);
      }
      out.push(radioGroup(types, "Chart type"));
    }
    out.push(comparePart(current));
    return out;
  }

  /** RPTCOMPARE1: the optional Compare choice, with the ? help popover (principle 6). Off by default; the results show it beside each number. */
  function comparePart(current: FlowCatalog): HTMLElement {
    const part = node("div", "rb-compare");
    part.dataset["testid"] = "rb-compare";
    const label = node("div", "rx-label rb-gap", "Compare (optional)");
    help(label, "compare");
    part.append(label);
    const allowed = compareAllowed(flow, current);
    const options = node("div", "rx-seg");
    for (const choice of COMPARE_OPTIONS) {
      const on = flow.extras.compare === choice.value;
      const toggle = button(choice.label, on ? "on" : "", () => change(setCompare(flow, choice.value, current)), `rb-compare-${choice.value === "" ? "none" : choice.value}`);
      segRadio(toggle, on);
      toggle.disabled = choice.value !== "" && !allowed;
      options.append(toggle);
    }
    part.append(radioGroup(options, "Compare"));
    if (!allowed && flow.sources.length > 0) part.append(node("p", "rb-muted", "A report that is as of now has no earlier period to compare with."));
    return part;
  }

  // ----- step 5
  async function loadChoices(): Promise<void> {
    if (choicesRequested) return;
    choicesRequested = true;
    const captured = generation;
    const [audience, delivery] = await Promise.allSettled([
      host.request<unknown>("GET", `${host.customPrefix}/audience-choices`),
      host.request<unknown>("GET", `${host.customPrefix}/schedules`),
    ]);
    if (!active() || captured !== generation) return;
    if (audience.status === "fulfilled" && audience.value.status === 200 && isRecord(audience.value.body) && Array.isArray(audience.value.body["items"])) {
      people = (audience.value.body["items"] as unknown[]).filter((entry): entry is Choice => isRecord(entry) && typeof entry["id"] === "string" && typeof entry["name"] === "string");
    } else {
      people = [];
      peopleNote = "People could not load.";
    }
    if (delivery.status === "fulfilled" && delivery.value.status === 200 && isRecord(delivery.value.body)) {
      const body = delivery.value.body;
      const recipients = Array.isArray(body["recipients"]) ? (body["recipients"] as unknown[]).filter((entry): entry is Choice => isRecord(entry) && typeof entry["id"] === "string" && typeof entry["name"] === "string") : [];
      const zones = Array.isArray(body["timezoneScopes"]) ? body["timezoneScopes"].length : 0;
      const reason = body["canManage"] === false
        ? "You do not have permission to manage report schedules."
        : body["canManage"] !== true || !Array.isArray(body["recipients"]) || !Array.isArray(body["timezoneScopes"])
          ? "Scheduling is unavailable in this builder."
          : recipients.length === 0
            ? "No eligible email recipients are available."
            : zones === 0
              ? "No scheduling location or network scope is available."
              : "";
      schedule = { recipients, available: body["canManage"] === true && recipients.length > 0 && zones > 0, reason };
    } else {
      schedule = { recipients: [], available: false, reason: "Scheduling options could not be loaded." };
    }
    if (step === 5 && view === "steps") {
      drawLeft();
      updateNav();
    }
  }
  function savePane(current: FlowCatalog): HTMLElement[] {
    const out: HTMLElement[] = [heading("Save it")];
    out.push(node("div", "rx-label rb-gap", "Name"));
    const name = node("input", "rx-field");
    name.type = "text";
    name.maxLength = 160;
    name.value = flow.name;
    name.setAttribute("aria-label", "Report name");
    name.dataset["testid"] = "rb-name";
    name.addEventListener("input", () => {
      flow = { ...flow, name: name.value };
      saveError = "";
      updateNav();
    });
    out.push(name);
    out.push(toggleSwitch("Add to favorites", flow.favorite, () => change({ ...flow, favorite: !flow.favorite }), "rb-favorite"));
    const shareHead = node("div", "rx-label rb-gap rb-row");
    shareHead.append(help(node("span", undefined, "Share"), "share"));
    out.push(shareHead);
    const share = node("div", "rx-seg");
    const named = people !== undefined && people.length > 0;
    for (const choice of [
      { id: "me" as const, label: "Only me" },
      { id: "named" as const, label: "Named people" },
    ]) {
      const toggle = button(choice.label, flow.share === choice.id ? "on" : "", () => change({ ...flow, share: choice.id }), `rb-share-${choice.id}`);
      segRadio(toggle, flow.share === choice.id);
      toggle.disabled = choice.id === "named" && !named;
      share.append(toggle);
    }
    out.push(radioGroup(share, "Share with"));
    if (flow.share === "named") {
      const chips = node("div", "rb-chips rb-gap");
      for (const person of people ?? []) {
        chips.append(chip(person.name, flow.people.includes(person.id), () => change({ ...flow, people: flow.people.includes(person.id) ? flow.people.filter((id) => id !== person.id) : [...flow.people, person.id] }), `rb-person-${person.id}`));
      }
      out.push(chips);
    }
    if (peopleNote !== "") out.push(node("p", "rx-notice", peopleNote));
    const scheduleOn = flow.schedule.on;
    const switchRow = node("div", "rb-row rb-gap");
    const sw = toggleSwitch("Email it on a schedule", scheduleOn, () => change({ ...flow, schedule: { ...flow.schedule, on: !scheduleOn } }), "rb-schedule-switch");
    sw.disabled = schedule === undefined || !schedule.available;
    switchRow.append(sw);
    help(switchRow, "schedule");
    out.push(switchRow);
    if (sw.disabled) {
      const reason = node("p", "rb-muted", schedule?.reason ?? "Loading scheduling options...");
      reason.id = scheduleReasonId;
      reason.dataset["testid"] = "rb-schedule-reason";
      sw.setAttribute("aria-describedby", scheduleReasonId);
      out.push(reason);
    }
    if (scheduleOn && schedule !== undefined) out.push(scheduleControls(schedule));
    if (saveError !== "") {
      const failure = node("p", "rx-notice", saveError);
      failure.setAttribute("role", "alert");
      failure.dataset["testid"] = "rb-save-error";
      out.push(failure);
    }
    saveReason = node("p", "rb-muted");
    saveReason.id = saveReasonId;
    saveReason.dataset["testid"] = "rb-save-reason";
    saveReason.setAttribute("role", "status");
    out.push(saveReason);
    void current;
    return out;
  }
  function toggleSwitch(label: string, on: boolean, action: () => void, testid: string): HTMLButtonElement {
    const element = node("button", `rx-switch${on ? " on" : ""}`);
    element.type = "button";
    element.setAttribute("role", "switch");
    element.setAttribute("aria-checked", String(on));
    element.dataset["testid"] = testid;
    element.append(node("i"), node("span", undefined, label));
    element.addEventListener("click", action);
    return element;
  }
  function scheduleControls(choices: ScheduleChoices): HTMLElement {
    const box = node("div", "rb-schedule");
    box.dataset["testid"] = "rb-schedule";
    const set = (patch: Partial<FlowState["schedule"]>): void => change({ ...flow, schedule: { ...flow.schedule, ...patch } });
    const current = flow.schedule;
    const cadence = node("div", "rx-seg");
    for (const choice of [
      { id: "daily" as const, label: "Daily" },
      { id: "weekly" as const, label: "Weekly" },
      { id: "monthly" as const, label: "Monthly" },
    ]) {
      const toggle = button(choice.label, current.cadence === choice.id ? "on" : "", () => set({ cadence: choice.id }), `rb-cadence-${choice.id}`);
      segRadio(toggle, current.cadence === choice.id);
      cadence.append(toggle);
    }
    box.append(radioGroup(cadence, "How often"));
    if (current.cadence === "weekly") {
      const days = node("div", "rb-chips rb-gap");
      WEEKDAY_NAMES.forEach((name, at) => days.append(chip(name, current.weekday === at, () => set({ weekday: at }), `rb-weekday-${at}`, false, "radio")));
      box.append(radioGroup(days, "Day of the week"));
    }
    if (current.cadence === "monthly") {
      const grid = node("div", "rb-days rb-gap");
      for (const day of MONTH_DAYS) {
        const cell = chip(monthDayLabel(day), current.monthDay === day, () => set({ monthDay: day }), `rb-day-${day}`, false, "radio");
        if (day === LAST_DAY) cell.classList.add("wide");
        grid.append(cell);
      }
      box.append(radioGroup(grid, "Day of the month"));
    }
    box.append(node("div", "rx-label rb-gap", "Time"));
    const times = node("div", "rb-chips");
    for (const time of SCHEDULE_TIMES) times.append(chip(time.label, current.time === time.value, () => set({ time: time.value }), `rb-time-${time.value}`, false, "radio"));
    box.append(radioGroup(times, "Time"));
    const delivery = node("div", "rx-seg rb-gap");
    for (const choice of [
      { id: "email" as const, label: "Email" },
      { id: "link" as const, label: "Link only" },
    ]) {
      const toggle = button(choice.label, current.delivery === choice.id ? "on" : "", () => set({ delivery: choice.id }), `rb-delivery-${choice.id}`);
      segRadio(toggle, current.delivery === choice.id);
      delivery.append(toggle);
    }
    box.append(radioGroup(delivery, "Delivery"));
    if (current.delivery === "email") {
      const attach = node("div", "rx-seg rb-gap");
      for (const format of ["csv", "xlsx", "pdf"] as const) {
        const toggle = button(format.toUpperCase(), current.attachment === format ? "on" : "", () => set({ attachment: format }), `rb-attach-${format}`);
        segRadio(toggle, current.attachment === format);
        attach.append(toggle);
      }
      box.append(radioGroup(attach, "Attachment"));
    }
    box.append(node("div", "rx-label rb-gap", "Recipients"));
    const chips = node("div", "rb-chips");
    for (const person of choices.recipients) {
      chips.append(chip(person.name, current.recipientIds.includes(person.id), () => set({ recipientIds: current.recipientIds.includes(person.id) ? current.recipientIds.filter((id) => id !== person.id) : [...current.recipientIds, person.id] }), `rb-recipient-${person.id}`));
    }
    box.append(chips);
    return box;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Save: create first, then each later step on its own, so a failure never loses the report
  // ---------------------------------------------------------------------------------------------------------------------
  async function save(): Promise<void> {
    if (saving || catalog === undefined) return;
    const problem = saveProblem(flow, catalog);
    if (problem !== "") {
      saveError = problem;
      drawLeft();
      return;
    }
    const built = flowSpec(flow, catalog);
    if (!built.ok) return;
    saving = true;
    saveError = "";
    updateNav();
    if (saveButton !== undefined) saveButton.textContent = "Saving";
    const plan = savePlan(flow, { share: people !== undefined && people.length > 0, schedule: schedule?.available === true });
    outcomes = [];
    const captured = generation;
    try {
      const editing = host.editing;
      const reply =
        editing === undefined
          ? await host.request<unknown>("POST", `${host.customPrefix}/saved`, { spec: built.spec, visibility: "private", requestId })
          : await host.request<unknown>("POST", `${host.customPrefix}/saved/${encodeURIComponent(editing.id)}/versions`, { spec: built.spec, baseVersion: editing.baseVersion });
      if (!active() || captured !== generation) return;
      const body = reply.body;
      const version = isRecord(body) ? Number(body["currentVersion"]) : NaN;
      if ((reply.status !== 200 && reply.status !== 201) || !isRecord(body) || typeof body["id"] !== "string" || !Number.isSafeInteger(version) || version < 1) {
        saving = false;
        saveError = message(reply, "That report did not save.");
        drawLeft();
        updateNav();
        if (saveButton !== undefined) saveButton.textContent = host.editing === undefined ? "Save report" : "Save new version";
        return;
      }
      const audience = body["audience"];
      savedSpec = built.spec;
      saved = { id: body["id"], version, audienceRevision: isRecord(audience) && typeof audience["revision"] === "number" ? audience["revision"] : 1 };
      outcomes.push({ step: "create", ok: true, message: "" });
      for (const stepId of plan.filter((id) => id !== "create")) outcomes.push(await runSaveStep(stepId));
    } catch {
      if (!active()) return;
      saving = false;
      saveError = "That report did not save.";
      drawLeft();
      updateNav();
      return;
    }
    if (!active()) return;
    saving = false;
    view = "done";
    draw();
  }
  async function runSaveStep(id: SaveStepId): Promise<SaveOutcome> {
    if (saved === undefined) return { step: id, ok: false, message: "That report did not save." };
    try {
      if (id === "audience") {
        if (host.setAudience === undefined) return { step: id, ok: false, message: "Sharing is unavailable." };
        const reply = await host.setAudience(saved.id, { mode: "named", recipientIds: flow.people, baseAudienceRevision: saved.audienceRevision });
        return reply.status === 200 ? { step: id, ok: true, message: "" } : { step: id, ok: false, message: message(reply, "Sharing did not apply.") };
      }
      if (id === "schedule") {
        const reply = await host.request<unknown>("POST", `${host.customPrefix}/schedules/save`, scheduleBody(flow.schedule, saved.id, saved.version));
        return reply.status === 200 ? { step: id, ok: true, message: "" } : { step: id, ok: false, message: message(reply, "The schedule did not save.") };
      }
      if (id === "favorite") {
        const reply = await host.request<unknown>("POST", `${host.marksPrefix}/favorite`, { reportId: saved.id, favorite: true });
        return reply.status === 200 ? { step: id, ok: true, message: "" } : { step: id, ok: false, message: message(reply, "The favorite did not save.") };
      }
    } catch {
      return { step: id, ok: false, message: "That did not apply. Try again." };
    }
    return { step: id, ok: true, message: "" };
  }
  const STEP_WORDS: Readonly<Record<SaveStepId, string>> = { create: "Saving", audience: "Sharing", schedule: "The schedule", favorite: "The favorite" };
  async function retry(id: SaveStepId): Promise<void> {
    const result = await runSaveStep(id);
    if (!active()) return;
    outcomes = outcomes.map((entry) => (entry.step === id ? result : entry));
    draw();
  }

  // ----- done
  function doneScreen(): HTMLElement {
    const card = node("div", "rx-card rb-done");
    card.dataset["testid"] = "rb-done";
    const check = node("div", "rb-check", "✓");
    check.setAttribute("aria-hidden", "true");
    card.append(check, node("h2", undefined, host.editing === undefined ? "Report saved" : "New version saved"));
    const scheduled = outcomes.some((entry) => entry.step === "schedule" && entry.ok);
    card.append(node("p", "rb-muted", `${flow.name.trim()}${scheduled ? ". Emails start on schedule." : ""}`));
    for (const entry of outcomes.filter((candidate) => !candidate.ok)) {
      const row = node("div", "rb-fail");
      row.dataset["testid"] = `rb-fail-${entry.step}`;
      row.setAttribute("role", "alert");
      row.append(node("span", undefined, `${STEP_WORDS[entry.step]} did not apply. ${entry.message}`), button("Try again", "rx-btn", () => void retry(entry.step), `rb-retry-${entry.step}`));
      card.append(row);
    }
    const actions = node("div", "rb-actions");
    actions.append(
      button("Run it now", "rx-btn rx-btn-primary rb-big-btn", () => {
        if (saved !== undefined) host.openSaved(saved.id, saved.version);
      }, "rb-run-now"),
      button("Back to Reports", "rx-btn rb-big-btn", () => host.back(), "rb-done-back"),
    );
    // REPORTUX1 (baseline 11): the saved report can be reopened for another change from here, as a new version.
    const editReport = host.editReport;
    if (editReport !== undefined && saved !== undefined) {
      const savedNow = saved;
      actions.append(button("Edit report", "rx-btn rb-big-btn", () => editReport({ spec: savedSpec, id: savedNow.id, baseVersion: savedNow.version }), "rb-done-edit"));
    }
    card.append(actions);
    return card;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // The preview card
  // ---------------------------------------------------------------------------------------------------------------------
  function drawPreview(): void {
    const box = previewBox;
    if (box === undefined || catalog === undefined || !active()) return;
    const head = node("div", "rb-row");
    head.append(node("h3", undefined, "Live preview"));
    const stamp = node("span", `rb-live${previewStatus === "loading" ? " busy" : ""}`, previewStatus === "loading" ? "Updating" : previewStatus === "ok" || previewStatus === "empty" ? "Updated" : "");
    stamp.setAttribute("aria-live", "polite");
    stamp.dataset["testid"] = "rb-preview-stamp";
    head.append(stamp);
    help(head, "preview");
    const summary = node("dl", "rb-summary");
    summary.dataset["testid"] = "rb-summary";
    for (const line of previewSummary(flow, catalog)) {
      const entry = node("div", "rb-summary-line");
      entry.append(node("dt", undefined, line.label), node("dd", undefined, line.value));
      summary.append(entry);
    }
    box.replaceChildren(head, summary, previewBody(catalog));
  }
  function previewBody(current: FlowCatalog): HTMLElement {
    if (previewStatus === "idle") {
      const blank = node("div", "rb-preview-blank");
      blank.dataset["testid"] = "rb-preview-idle";
      return blank;
    }
    if (previewStatus === "loading") {
      const wrap = node("div", "rb-preview-body");
      wrap.dataset["testid"] = "rb-preview-loading";
      wrap.setAttribute("role", "status");
      wrap.setAttribute("aria-busy", "true");
      wrap.setAttribute("aria-label", "Loading the preview");
      for (const width of ["100%", "100%", "80%", "60%"]) {
        const line = node("div", "rx-skel rb-skel");
        line.style.width = width;
        wrap.append(line);
      }
      return wrap;
    }
    if (previewStatus === "busy") {
      const state = node("div", "rb-preview-state");
      state.dataset["testid"] = "rb-preview-busy";
      state.setAttribute("role", "status");
      const title = node("h4", undefined, "Another report is running here");
      help(title, "oneAtATime");
      state.append(title, button("Try again", "rx-btn rx-btn-primary", () => {
        previewSpecRan = undefined;
        void runPreview();
      }, "rb-preview-retry"));
      return state;
    }
    if (previewStatus === "denied" || previewStatus === "error") {
      const state = node("div", "rb-preview-state");
      state.dataset["testid"] = `rb-preview-${previewStatus}`;
      state.setAttribute("role", "alert");
      state.append(node("h4", undefined, previewStatus === "denied" ? "No access to run this report" : "The preview did not load"), node("p", undefined, previewStatus === "denied" ? "Ask an admin." : previewNote));
      if (previewStatus === "error") {
        state.append(button("Try again", "rx-btn rx-btn-primary", () => {
          previewSpecRan = undefined;
          void runPreview();
        }, "rb-preview-retry"));
      }
      return state;
    }
    if (previewStatus === "empty" || previewResult === undefined) {
      const state = node("div", "rb-preview-state");
      state.dataset["testid"] = "rb-preview-empty";
      state.append(node("h4", undefined, "Nothing recorded in these dates"));
      return state;
    }
    const wrap = node("div", "rb-preview-body");
    const slot = chartOf(previewResult.spec);
    if (slot !== undefined) {
      const datasetForLabels: CatalogDataset | undefined = datasetOf(current, previewResult.meta.dataset);
      const chart = node("div", "rb-preview-chart");
      chart.dataset["testid"] = "rb-preview-chart";
      chart.append(renderChartCard(buildChartModel(compositePresentation(previewResult), slot, valueLabelsOf(datasetForLabels)), CHART_WIDTH_BUILDER - 120));
      wrap.append(chart);
    }
    const table = previewTable(previewResult, current, slot === undefined ? 6 : 3);
    const scroll = node("div", "rb-preview-scroll");
    scroll.tabIndex = 0;
    scroll.setAttribute("aria-label", "Preview table");
    const element = node("table", "rx-table rb-table");
    element.dataset["testid"] = "rb-preview-table";
    const thead = node("thead");
    const headRow = node("tr");
    for (const column of table.heads) {
      const th = node("th", column.figure ? "num" : "", column.label);
      th.scope = "col";
      headRow.append(th);
    }
    thead.append(headRow);
    element.append(thead);
    const tbody = node("tbody");
    for (const row of table.rows) {
      const tr = node("tr");
      tr.dataset["testid"] = "rb-preview-row";
      row.forEach((cell, at) => tr.append(node("td", table.heads[at]?.figure === true ? "num rx-num" : "", cell)));
      tbody.append(tr);
    }
    element.append(tbody);
    if (table.total !== null) {
      const tfoot = node("tfoot");
      const tr = node("tr", "rx-total-row");
      tr.dataset["testid"] = "rb-preview-total";
      table.total.forEach((cell, at) => tr.append(node("td", table.heads[at]?.figure === true ? "num rx-num" : "", cell)));
      tfoot.append(tr);
      element.append(tfoot);
    }
    scroll.append(element);
    wrap.append(scroll);
    wrap.append(node("div", "rx-foot", table.more > 0 ? `${table.count}, showing ${table.rows.length}` : table.count));
    return wrap;
  }

  void load();
  return {
    destroy() {
      alive = false;
      generation++;
      if (previewTimer !== undefined) clearTimeout(previewTimer);
      hideHoverHelp();
      root.remove();
    },
  };
}
